// On a phone, a screen scrolls as one (UX-38, B-309, DIRECTION U20).
//
// A screen that opts in carries `fsp-flow` on its `.fsp-screen`; on a phone
// (the frame's `data-tier`, shell.css) the screen itself scrolls under the top
// bar and the tabs, its header and status scroll away, and at most one row of
// the list's own controls — `.fsp-pinned-stick`, the segments or the filters
// — sticks to its top. The column names stick under that row (UX-36), so
// they need its height, which no stylesheet knows: the filters wrap, the
// text grows. This measures it, and hands it to the stylesheet as
// `--fsp-stuck-h` on the screen.
//
// IN THE KIT because it writes a style property, which a clean screen never
// does (LOOKS.md Part 7). The shell runs it once over its content area, so a
// screen opts in with classes alone.

/** Watch `main` for flow screens and keep each one's `--fsp-stuck-h` true. */
export function watchPhoneFlow(main: HTMLElement): () => void {
  const sizes = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const stick = entry.target as HTMLElement;
      const screen = stick.parentElement;
      if (screen === null) continue;
      screen.style.setProperty('--fsp-stuck-h', `${stick.getBoundingClientRect().height}px`);
    }
  });
  let watched = new Set<Element>();
  const scan = () => {
    const now = new Set<Element>(main.querySelectorAll('.fsp-flow > .fsp-pinned-stick'));
    for (const el of watched) if (!now.has(el)) sizes.unobserve(el);
    for (const el of now) {
      if (watched.has(el)) continue;
      sizes.observe(el);
      // At once, not at the next frame's resize: a screen that opens on a
      // section (Rules' `?section=`) scrolls to it as soon as it is drawn.
      const screen = el.parentElement;
      if (screen !== null) {
        screen.style.setProperty('--fsp-stuck-h', `${el.getBoundingClientRect().height}px`);
      }
    }
    watched = now;
    // A screen whose sticky row went away sticks its heads to its top again.
    for (const screen of main.querySelectorAll<HTMLElement>('.fsp-flow')) {
      if (screen.querySelector(':scope > .fsp-pinned-stick') === null) {
        screen.style.removeProperty('--fsp-stuck-h');
      }
    }
  };
  // Screens come and go with every route and every load; the observer hands
  // over a render's changes together, once they are in place.
  const changes = new MutationObserver(scan);
  changes.observe(main, { childList: true, subtree: true });
  scan();
  return () => {
    changes.disconnect();
    sizes.disconnect();
    // Off the phone (turned to landscape, a tablet's width), no row sticks.
    for (const screen of main.querySelectorAll<HTMLElement>('.fsp-flow')) {
      screen.style.removeProperty('--fsp-stuck-h');
    }
  };
}
